import React, { useMemo, useRef, useState } from 'react';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createTRPCClient, httpLink } from '@trpc/client';
import { useDispatch, useStore } from 'react-redux';
import superjson from 'superjson';
import { RequestHeaders } from 'fym-common/src/utils/fetch-types';
import { TypedMemo } from '../../common/types';
import { config } from '../../../config/config';
import { makeClientDescribable } from '../../../trpc/describableClient';
import { TRPCProviderAPI } from '../../../trpc/trpc';
import type { TRPCAPIRouter } from '../../../../../music24-api/src/trpc/router';
import { getTimezone } from '../../../utils/deviceInfo/getTimezone';
import { appInformation } from '../../../utils/appInformation/appInformation';
import { useUserToken } from '../../../redux/slices/appInfo/hooks';
import { dispatchM24LimitPrompt, dispatchM24LimitPromptForMutation } from '../../music24/utils/parseM24LimitError';
import { TRPC_API_URL } from '../../../trpc/trpcUrl';
import { createRefreshingFetch } from '../session/createRefreshingFetch';
import { WorkspaceChangeReset } from './WorkspaceChangeReset';
import type { RootStateType } from '../../../redux/store';
import { selectAccountId } from '../../../redux/slices/appInfo/selectors';
import { demoArtistHeaders } from '../../../trpc/demoArtistHeader';
import { DemoArtistQueryReset } from '../../../trpc/DemoArtistQueryReset';

const TRPCLoaderComponent: React.FC<React.PropsWithChildren> = ({ children }) => {
  const userToken = useUserToken();
  const dispatch = useDispatch();
  const store = useStore<RootStateType>();
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  // Every tRPC query or mutation error passes through here, so a plan-limit refusal opens the
  // upgrade prompt no matter which screen or hook triggered the call — unless the mutation opts
  // out via `meta: { skipM24LimitPrompt: true }` (`dispatchM24LimitPromptForMutation`). A query
  // that runs in the background (the Outreach inbox badge) opts out the same way.
  const [queryClient] = useState(() => {
    const onQueryError = (error: unknown, query: { meta?: Record<string, unknown> }) => {
      if (query.meta?.skipM24LimitPrompt === true) {
        return;
      }
      dispatchM24LimitPrompt(dispatchRef.current, error);
    };
    return new QueryClient({
      defaultOptions: { queries: { retry: false } },
      queryCache: new QueryCache({ onError: onQueryError }),
      mutationCache: new MutationCache({
        onError: (error, _variables, _context, mutation) =>
          dispatchM24LimitPromptForMutation(dispatchRef.current, error, mutation),
      }),
    });
  });

  // One `fetch` for the life of the loader: a 401 from music24-api refreshes the session once.
  const [refreshingFetch] = useState(() => createRefreshingFetch((action) => dispatchRef.current(action)));

  const allHeaders = useMemo(() => {
    const headers: RequestHeaders = {
      'x-appinfo-target': config.target,
      'x-appinfo-platform': config.platform,
      'x-appinfo-environment': config.environment,
      'x-appinfo-tz': getTimezone(),
    };
    const { version, platformVersion, buildVersion } = appInformation;
    if (version) {
      headers['x-appinfo-version'] = version;
    }
    if (platformVersion) {
      headers['x-appinfo-platformVersion'] = platformVersion;
    }
    if (buildVersion) {
      headers['x-appinfo-buildVersion'] = buildVersion;
    }

    return headers;
  }, []);

  const trpcAPIClient = useMemo(() => {
    const headers = {
      ...allHeaders,
      ...(userToken ? { Authorization: `Bearer ${userToken}` } : {}),
    };
    return createTRPCClient<TRPCAPIRouter>({
      links: [
        httpLink({
          transformer: superjson,
          url: TRPC_API_URL,
          // Read the active artist per request: the demo artist's reads keyed by the workspace
          // answer demo data (see trpc/demoArtistHeader.ts).
          headers: () => ({ ...headers, ...demoArtistHeaders(selectAccountId(store.getState())) }),
          fetch: refreshingFetch,
        }),
      ],
    });
  }, [allHeaders, userToken, refreshingFetch, store]);

  // React's render logging walks these props; see describableClient.ts. The analytics procedures
  // live on music24-api too, so one client and one provider serve the whole app.
  const describableAPIClient = useMemo(() => makeClientDescribable(trpcAPIClient), [trpcAPIClient]);

  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProviderAPI trpcClient={describableAPIClient} queryClient={queryClient}>
        {children}
        <WorkspaceChangeReset />
        <DemoArtistQueryReset />
      </TRPCProviderAPI>
    </QueryClientProvider>
  );
};

export const TRPCLoader = TypedMemo(TRPCLoaderComponent);
